import React, { useMemo, useEffect } from 'react';
import { useFormContext } from 'react-hook-form-mui';
import { beautifyEnumWithTHC } from 'src/utils/utility';
import { CHARGE_DOMAIN } from '../actionDetails/mainDetails/deviations/procurement/NewChargeContext';
import { RhfMuiSelectInput } from '../../molecules/fields/rhf-mui-fields/RhfMuiSelectInput';
import { useChargeDomainsByDeviation } from 'src/use-cases/charges/useChargeDomains';
import { useShipmentSelector } from '../actionDetails/ShipmentSelector';
import { useQuestionnaire } from 'src/use-cases/invoice/useQuestionnaire';

interface LegAndDomainAssignmentProps {
  disabled?: boolean;
  deviationId?: string;
  questionFieldName?: string; // Name of the question field to get the answer type from
}

export const LegAndDomainAssignment: React.FC<LegAndDomainAssignmentProps> = ({ disabled = false, deviationId, questionFieldName }) => {
  const { watch, setValue } = useFormContext();
  const selectedLegType = watch('legType');
  const { shipment } = useShipmentSelector();

  // Get the user's selected answer option ID for the question
  const selectedAnswerOptionId = questionFieldName ? watch(questionFieldName) : undefined;

  // Fetch the questionnaire to get question details
  const { data: questionnaireData } = useQuestionnaire(deviationId || '');

  // Extract the answer type from the selected answer option
  const questionAnswerType = useMemo(() => {
    if (!selectedAnswerOptionId || !questionnaireData?.data) {
      return undefined;
    }

    // Search through all workflows to find the question with DO_YOU_ACCEPT_THIS_NEW_CHARGE_AS_IT domain type
    const allQuestions = Object.values(questionnaireData.data).flat();
    const targetQuestion = allQuestions.find((q) => q.questionDomainType === 'DO_YOU_ACCEPT_THIS_NEW_CHARGE_AS_IT');

    if (!targetQuestion?.answerOptions) {
      return undefined;
    }

    // Find the selected answer option and return its type
    const selectedOption = targetQuestion.answerOptions.find((option) => option.id === selectedAnswerOptionId);
    return selectedOption?.type;
  }, [selectedAnswerOptionId, questionnaireData]);

  // Detect conditions for using the new API (charge-domains?leg-type=X):
  // 1. Must have contract and shipping service (NOT NRCF)
  // 2. Answer type must be 'OTHER'
  // 3. Must have selected a leg type
  const isNrcf = !shipment?.contractId || !shipment?.shippingServiceId;
  const shouldUseNewAPI = !!deviationId && !!selectedLegType && questionAnswerType === 'OTHER' && !isNrcf;

  // Fetch charge domains from new API (conditional based on requirements)
  const { data: chargeDomainResponse, isLoading: isLoadingDomains } = useChargeDomainsByDeviation(
    deviationId,
    selectedLegType,
    shouldUseNewAPI // Only call new API when conditions are met
  );

  // Prepare options for leg assignment
  const legOptions = [
    { id: 'LEG_ONE', label: 'Leg 1' },
    { id: 'LEG_TWO', label: 'Leg 2' },
    { id: 'LEG_THREE', label: 'Leg 3' },
  ];

  // Prepare charge domain options based on API response or fallback to static CHARGE_DOMAIN list
  const chargeDomainOptions = useMemo(() => {
    // Use new API data if:
    // - We called the new API (shouldUseNewAPI is true)
    // - AND we have valid data from the API
    if (shouldUseNewAPI && chargeDomainResponse?.data && chargeDomainResponse.data.length > 0) {
      return chargeDomainResponse.data.map((domain) => ({
        id: domain,
        label: beautifyEnumWithTHC(domain),
      }));
    }

    // Fallback to static CHARGE_DOMAIN list when:
    // 1. No leg type selected yet
    // 2. Answer type is NOT 'OTHER'
    // 3. Primary API fails or returns no data
    // 4. NRCF (no contract/shipping service)
    return CHARGE_DOMAIN.map((option) => ({
      id: option,
      label: beautifyEnumWithTHC(option),
    }));
  }, [shouldUseNewAPI, chargeDomainResponse]);

  // Reset charge domain when leg type changes (only when using new API)
  useEffect(() => {
    if (selectedLegType && shouldUseNewAPI) {
      // Clear the charge domain selection when leg type changes and we're calling the API
      setValue('chargeDomain', '');
    }
  }, [selectedLegType, shouldUseNewAPI, setValue]);

  return (
    <div className='mt-24 space-y-4'>
      <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
        <RhfMuiSelectInput name='legType' fullWidth label='Select leg assignment' options={legOptions} disabled={disabled} required />
        <RhfMuiSelectInput
          name='chargeDomain'
          fullWidth
          label='Select domain assignment'
          options={chargeDomainOptions}
          disabled={disabled || (shouldUseNewAPI && (!selectedLegType || isLoadingDomains))}
          required
        />
      </div>
    </div>
  );
};
